import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Shared Markdown → HTML renderer for the places that show ticket / job / note
 * text outside the Toast UI viewer: pickers, table cells, summaries.
 *
 * Options:
 *   maxLines – keep only the first N source lines and append an ellipsis when
 *              something was cut (ticket pickers, list rows).
 *   compact  – smaller type and tighter spacing for table cells.
 *
 * Output is sanitised with DOMPurify before it is handed to v-html.
 */

const normalise = (text) => String(text ?? '')
  .replace(/\r\n?/g, '\n')
  .split('\n')
  .map((line) => line
    .replace(/^(\s+)-/, '\t-') // indented "-" list items → tab-indented
    .replace(/^(#{1,6})([^\s#])/, '$1 $2')) // "###Heading" → "### Heading"
  .join('\n');

const STYLES = {
  default: {
    'h1, h2': ['mb-2', 'text-lg', 'font-semibold', 'text-gray-900', 'dark:text-white'],
    'h3, h4, h5, h6': ['mb-2', 'text-base', 'font-semibold', 'text-gray-900', 'dark:text-white'],
    p: ['mb-2'],
    ul: ['mb-2', 'space-y-1', 'list-disc', 'list-inside'],
    ol: ['mb-2', 'space-y-1', 'list-decimal', 'list-inside'],
    'ul ul, ol ul, ul ol, ol ol': ['ml-4', 'mt-1', 'border-l', 'pl-4', 'border-gray-200', 'dark:border-gray-700'],
    pre: ['mb-2', 'p-2', 'rounded', 'text-xs', 'whitespace-pre-wrap', 'break-words', 'bg-gray-100', 'dark:bg-gray-800'],
    a: ['text-blue-600', 'dark:text-blue-400', 'underline'],
    table: ['mb-2', 'text-xs', 'border-collapse'],
    'th, td': ['border', 'border-gray-300', 'dark:border-gray-600', 'px-1'],
  },
  compact: {
    'h1, h2, h3, h4, h5, h6': ['mb-1', 'text-sm', 'font-semibold'],
    p: ['mb-1'],
    ul: ['mb-1', 'list-disc', 'list-inside'],
    ol: ['mb-1', 'list-decimal', 'list-inside'],
    'ul ul, ol ul, ul ol, ol ol': ['ml-3'],
    pre: ['mb-1', 'p-1', 'rounded', 'text-xs', 'whitespace-pre-wrap', 'break-words', 'bg-gray-100', 'dark:bg-gray-800'],
    code: ['text-xs'],
    a: ['text-blue-600', 'dark:text-blue-400', 'underline'],
    table: ['mb-1', 'text-xs', 'border-collapse'],
    'th, td': ['border', 'border-gray-300', 'dark:border-gray-600', 'px-1'],
  },
};

export function renderMarkdown(text, { maxLines = null, compact = false } = {}) {
  let source = normalise(text);
  if (!source.trim()) return '';

  let truncated = false;
  if (maxLines > 0) {
    const lines = source.split('\n');
    if (lines.length > maxLines) {
      source = lines.slice(0, maxLines).join('\n');
      truncated = true;
    }
  }

  const html = DOMPurify.sanitize(marked.parse(source, { gfm: true, breaks: true }), {
    USE_PROFILES: { html: true },
  });
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const styles = compact ? STYLES.compact : STYLES.default;
  Object.entries(styles).forEach(([selector, classes]) => {
    doc.body.querySelectorAll(selector).forEach((el) => el.classList.add(...classes));
  });
  doc.body.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });

  if (truncated) {
    const more = doc.createElement('p');
    more.className = 'text-xs italic text-gray-500 dark:text-gray-400';
    more.textContent = '…';
    doc.body.appendChild(more);
  }

  return doc.body.innerHTML;
}

export default renderMarkdown;
